import React, { useState } from "react";
import { Loader2, CheckCircle2, AlertCircle, Mail } from "lucide-react";
import { API } from "@/lib/api";

/**
 * MagicLinkLoginForm (Sprint 12+K, 07.07.2026).
 *
 * Wiederverwendbare Login-UI fuer Nicht-eingeloggte Besucher auf
 * /warteliste und /dashboard. Fordert einen Magic-Link an, respektiert
 * per Prop das Flow-Routing (`free` = 30d, `waitlist` = 14d).
 *
 * Props:
 *   flow: "free" | "waitlist" | "default"   — bestimmt Token-TTL
 *   heading: string                          — Sichtbare Ueberschrift
 *   testIdPrefix: string                     — Prefix fuer data-testid
 */
export function MagicLinkLoginForm({
  flow = "default",
  heading = "Schon angemeldet?",
  hint = "Ein Login-Link per E-Mail ist auf dem Weg.",
  testIdPrefix = "magic-login",
}) {
  const [email, setEmail] = useState("");
  const [state, setState] = useState("idle"); // idle | loading | sent | error
  const [error, setError] = useState("");

  async function submit(e) {
    e.preventDefault();
    if (!email.trim() || state === "loading") return;
    setState("loading");
    setError("");
    try {
      const resp = await fetch(`${API}/auth/magic/request`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: email.trim().toLowerCase(),
          source: `sprint12k_${flow}_login`,
          flow,
        }),
      });
      if (!resp.ok) {
        const body = await resp.json().catch(() => ({}));
        throw new Error(body?.detail || `HTTP ${resp.status}`);
      }
      setState("sent");
    } catch (err) {
      setError(String(err?.message || err) || "Da ist etwas schiefgelaufen.");
      setState("error");
    }
  }

  if (state === "sent") {
    return (
      <div
        className="border border-emerald-200 bg-emerald-50 rounded-2xl p-6 text-center"
        data-testid={`${testIdPrefix}-success`}
      >
        <CheckCircle2 className="w-8 h-8 text-emerald-500 mx-auto mb-3" />
        <p className="text-emerald-900 font-medium">Magic-Link verschickt.</p>
        <p className="mt-1 text-sm text-emerald-700">
          Check dein Postfach an <span className="font-semibold">{email}</span>.
          Der Link ist {flow === "free" ? "30 Tage" : flow === "waitlist" ? "14 Tage" : "15 Minuten"} gültig
          und einmalig einlösbar.
        </p>
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      className="border border-slate-200 bg-white rounded-2xl p-6 shadow-sm"
      data-testid={`${testIdPrefix}-form`}
    >
      <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
        <Mail className="w-5 h-5 text-slate-500" />
        {heading}
      </h3>
      <p className="mt-1 text-sm text-slate-500">{hint}</p>
      <div className="mt-4 flex flex-col sm:flex-row gap-2">
        <input
          type="email"
          required
          placeholder="deine@email.de"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={state === "loading"}
          data-testid={`${testIdPrefix}-email`}
          className="flex-1 rounded-lg border border-slate-300 px-3 py-2.5 text-sm focus:border-[#4563BB] focus:ring-2 focus:ring-[#4563BB]/20 outline-none disabled:bg-slate-50"
        />
        <button
          type="submit"
          disabled={state === "loading" || !email.trim()}
          data-testid={`${testIdPrefix}-submit`}
          className="inline-flex items-center justify-center gap-2 rounded-lg bg-[#4563BB] hover:bg-[#3a55a3] disabled:opacity-50 text-white font-medium px-4 py-2.5 text-sm transition-colors"
        >
          {state === "loading" ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Sende …
            </>
          ) : (
            "Magic-Link anfordern"
          )}
        </button>
      </div>
      {state === "error" && (
        <div
          className="mt-3 flex items-start gap-2 text-sm text-red-700"
          data-testid={`${testIdPrefix}-error`}
        >
          <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>{error}</span>
        </div>
      )}
    </form>
  );
}
